import React, { useState } from "react"

import { useTranslation } from "react-i18next"
import { useQuery } from "react-query"
import { useParams } from "react-router-dom"

import LaunchOutlinedIcon from "@mui/icons-material/LaunchOutlined"
import SubjectIcon from "@mui/icons-material/Subject"
import { Grid } from "@mui/material"
import { Button } from "@synapse-analytics/synapse-ui"
import type { AxiosError, AxiosResponse } from "axios"

import { ValueBlock } from "../../../../components/data-display/ValueBlock"
import { getTheme } from "../../../../hooks/UseTheme"
import { KonanAPI } from "../../../../services/KonanAPI"
import type { PaginatedProgramList } from "../../../../types/generated/api/PaginatedProgramList"
import type { Program } from "../../../../types/generated/api/Program"
import { useNodeDeletion } from "../../hooks/useNodeHooks"
import type { ProgramNodeProps, SelectionDataItemType } from "../../types/interfaces"
import { nodeHelpers } from "../../utils/nodeHelpers"
import { SelectDataMenu } from "./SelectDataMenu"
import { BaseWorkflowNode } from "./base/BaseWorkflowNode"

/**
 * ProgramNode component handles program selection and configuration in workflows.
 * Supports program selection and external navigation.
 */
export function ProgramNode(props: Readonly<ProgramNodeProps>): React.ReactElement {
  const { data, menuView, id } = props
  const updateGraph = props.data?.updateGraph
  const programs = data?.programs
  const readMode: boolean | null = data?.readMode ?? null
  const nodeId = id ?? null
  const currentDataItemId = data?.program

  // Navigation hooks
  const { id: projectId } = useParams<{ id: string }>()

  // Theme hook
  const theme = getTheme()

  // Translation hook
  const { t } = useTranslation("workflows", {
    keyPrefix: "selectionNode",
  })

  // Constants
  const page = 1
  const pageSize = 200

  // Get node metadata
  const metadata = nodeHelpers.getNodeMetadata("ProgramNode")

  // Use our custom hook for node deletion
  const { handleRemoveNode } = useNodeDeletion(nodeId as string, updateGraph)

  // Data fetching queries
  const { isLoading: isProgramsLoading, data: programsData } = useQuery<
    AxiosResponse<PaginatedProgramList>,
    AxiosError
  >(["programs", projectId, page, pageSize], () => KonanAPI.fetchPrograms(projectId as string, page, pageSize), {
    enabled: !!projectId && !menuView && !readMode && data?.nodeType === "ProgramNode",
  })

  const currentData = (): PaginatedProgramList | undefined => {
    return programsData?.data ?? programs
  }

  const [selectedValue, setSelectedValue] = useState<SelectionDataItemType | undefined | string>(
    currentDataItemId
      ? typeof currentDataItemId === "string"
        ? currentDataItemId
        : (currentDataItemId as Program)?.uuid
      : undefined,
  )

  return (
    <BaseWorkflowNode
      id={nodeId as string}
      menuView={menuView}
      readMode={readMode ?? undefined}
      nodeType="ProgramNode"
      title={t("nodeTitles.program")}
      iconProps={{
        element: <SubjectIcon sx={{ color: theme.palette.base.text[1] }} />,
        className: metadata.iconClassName,
      }}
      width={metadata.width}
      handlesProps={{ showTarget: metadata.showTargetHandle, showSource: metadata.showSourceHandle }}
      onRemove={() => handleRemoveNode(t("notifications.nodeRemoved", { nodeType: "program" }))}
    >
      {/* Node Content */}
      <Grid item xs={12} display="flex" justifyContent="space-between" alignItems="center">
        {/* Data Selection */}
        <Grid item xs={10}>
          {!menuView && !readMode ? (
            <SelectDataMenu
              nodeId={nodeId}
              dataType="ProgramNode"
              dataItemId={currentDataItemId}
              menuView={menuView}
              itemData={currentData()}
              selectedValue={selectedValue as string}
              setSelectedValue={setSelectedValue}
              isDataLoading={isProgramsLoading}
            />
          ) : (
            <Grid container justifyContent="space-between" alignItems="center">
              <ValueBlock
                value={
                  typeof currentDataItemId === "string"
                    ? currentDataItemId
                    : (currentDataItemId as Program)?.name || t("nodeBodies.noProgramSelected")
                }
                size={12}
                showTooltip={!menuView}
              />
            </Grid>
          )}
        </Grid>

        {/* Action Button - External Link */}
        <Grid item xs={2} ml={0.5}>
          <Button
            onClick={() =>
              setTimeout(() => {
                window.open(`/projects/${projectId}/Programs`, "_blank")
              })
            }
            disabled={menuView}
            size={"regular"}
            fullWidth
            data-mode={readMode ? "read" : menuView ? "edit-view" : "edit"}
            variant="secondary"
          >
            <LaunchOutlinedIcon fontSize="small" sx={{ color: theme.palette.base.text[1] }} />
          </Button>
        </Grid>
      </Grid>
    </BaseWorkflowNode>
  )
}
